import { Fragment, useState } from 'react';
import { cn } from '@/lib/utils';
import { EvidenceButton, EvidenceLines, type Lines } from '@/components/profile/restitution/Evidence';
import type { QualificationThemeKey } from '@shared/types/company-qualification';
import { type ScoreLevel, THEME_VISUALS, type ThemeVisual } from '@shared/types/company-restitution';

type VisualProps<K extends ThemeVisual['kind']> = { visual: Extract<ThemeVisual, { kind: K }>; lines: Lines };

/** One open element at a time per visual. */
const useOpen = () => {
  const [open, setOpen] = useState<number | null>(null);
  return { isOpen: (index: number) => open === index, toggle: (index: number) => setOpen(open === index ? null : index) };
};

const Funnel = ({ visual, lines }: VisualProps<'funnel'>) => {
  const { isOpen, toggle } = useOpen();
  const last = Math.max(visual.stages.length - 1, 1);
  return (
    <div className="space-y-1">
      {visual.stages.map((stage, index) => (
        <Fragment key={`${stage.label}-${index}`}>
          {index > 0 && (
            <p className="text-center text-xs text-domino-inkSoft">
              {stage.rate || 'taux inconnu'}{stage.assumed && <span className="ml-1.5 rounded-full bg-amber-50 px-1.5 text-amber-800">supposé</span>}
            </p>
          )}
          {/* Width shows the order of the stages, not their volume. */}
          <div className="mx-auto" style={{ width: `${100 - (index / last) * 58}%` }}>
            <div className="flex items-center justify-between gap-2 rounded-lg bg-domino-lilac px-3 py-2 text-sm text-domino-aubergine">
              <span><span className="font-semibold">{stage.value}</span> · {stage.label}</span>
              <EvidenceButton ids={stage.evidence} lines={lines} open={isOpen(index)} onToggle={() => toggle(index)} />
            </div>
            {isOpen(index) && <EvidenceLines ids={stage.evidence} lines={lines} className="mt-1" />}
          </div>
        </Fragment>
      ))}
      {visual.bottleneck && <p className="pt-3 text-sm text-domino-ink"><span className="font-semibold">Goulot : </span>{visual.bottleneck}</p>}
    </div>
  );
};

const Table = ({ visual, lines, theme }: VisualProps<'table'> & { theme: QualificationThemeKey }) => {
  const { isOpen, toggle } = useOpen();
  const columns = THEME_VISUALS[theme].columns ?? [];
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[36rem] border-collapse text-sm">
        <thead>
          <tr className="text-left text-xs text-domino-inkSoft">
            {columns.map((column) => <th key={column} className="px-2 pb-2 font-medium">{column}</th>)}
            <th className="w-20 pb-2" aria-label="Statut" />
          </tr>
        </thead>
        <tbody>
          {visual.rows.map((row, index) => (
            <Fragment key={`${row.cells[0]}-${index}`}>
              <tr className={cn('border-t border-domino-cardBorder align-top', row.highlight && 'bg-domino-lilac/50')}>
                {row.cells.map((cell, cellIndex) => (
                  <td key={cellIndex} className={cn('px-2 py-2 leading-6', cellIndex === 0 ? 'font-medium text-domino-ink' : 'text-domino-ink/90')}>{cell}</td>
                ))}
                <td className="px-2 py-2 text-right"><EvidenceButton ids={row.evidence} lines={lines} open={isOpen(index)} onToggle={() => toggle(index)} /></td>
              </tr>
              {isOpen(index) && <tr><td colSpan={columns.length + 1} className="px-2 pb-2"><EvidenceLines ids={row.evidence} lines={lines} /></td></tr>}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Steps = ({ visual, lines }: VisualProps<'steps'>) => {
  const { isOpen, toggle } = useOpen();
  return (
    <ol className="space-y-3">
      {visual.steps.map((step, index) => (
        <li key={`${step.label}-${index}`} className="flex gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-domino-lilac text-xs font-semibold text-domino-aubergine">{index + 1}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <p className="font-medium text-domino-ink">{step.label}</p>
              <EvidenceButton ids={step.evidence} lines={lines} open={isOpen(index)} onToggle={() => toggle(index)} />
            </div>
            <p className="text-xs text-domino-inkSoft">{[step.who, step.tool, step.time].filter((part) => part && !/^(inconnu|inconnue|à mesurer)$/i.test(part)).join(' · ')}</p>
            {step.issue && <p className="mt-1 text-sm text-amber-800">{step.issue}</p>}
            {isOpen(index) && <EvidenceLines ids={step.evidence} lines={lines} className="mt-1" />}
          </div>
        </li>
      ))}
    </ol>
  );
};

const LEVELS: Record<ScoreLevel, { label: string; dot: string }> = {
  good: { label: 'prêt', dot: 'bg-emerald-600' },
  watch: { label: 'à surveiller', dot: 'bg-amber-500' },
  bad: { label: 'bloquant', dot: 'bg-red-600' },
};

const Scorecard = ({ visual, lines }: VisualProps<'scorecard'>) => {
  const { isOpen, toggle } = useOpen();
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {visual.indicators.map((indicator, index) => (
        <div key={`${indicator.label}-${index}`} className="rounded-lg border border-domino-cardBorder bg-white/60 px-3 py-2.5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-domino-inkSoft">{indicator.label}</p>
            <EvidenceButton ids={indicator.evidence} lines={lines} open={isOpen(index)} onToggle={() => toggle(index)} />
          </div>
          <p className="mt-1 text-lg font-semibold text-domino-ink">{indicator.value}</p>
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-domino-inkSoft">
            <span className={cn('h-2 w-2 rounded-full', LEVELS[indicator.level].dot)} />{LEVELS[indicator.level].label}{indicator.note ? ` · ${indicator.note}` : ''}
          </p>
          {isOpen(index) && <EvidenceLines ids={indicator.evidence} lines={lines} className="mt-2" />}
        </div>
      ))}
    </div>
  );
};

const Cards = ({ visual, lines }: VisualProps<'cards'>) => {
  const { isOpen, toggle } = useOpen();
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {visual.cards.map((card, index) => (
        <div key={`${card.title}-${index}`} className="rounded-lg border border-domino-cardBorder bg-white/60 px-4 py-3">
          <div className="flex items-start justify-between gap-2">
            <p className="font-semibold text-domino-ink">{card.title}</p>
            <EvidenceButton ids={card.evidence} lines={lines} open={isOpen(index)} onToggle={() => toggle(index)} />
          </div>
          <dl className="mt-2 grid grid-cols-[7.5rem_1fr] gap-x-3 gap-y-1 text-sm">
            {card.facts.map((fact) => <Fragment key={fact.label}><dt className="text-domino-inkSoft">{fact.label}</dt><dd className="text-domino-ink">{fact.value}</dd></Fragment>)}
          </dl>
          {isOpen(index) && <EvidenceLines ids={card.evidence} lines={lines} className="mt-2" />}
        </div>
      ))}
    </div>
  );
};

/** The visual of a theme page, drawn by the app from the written restitution. */
export const ThemeVisualView = ({ theme, visual, lines }: { theme: QualificationThemeKey; visual: ThemeVisual; lines: Lines }) => {
  switch (visual.kind) {
    case 'funnel': return <Funnel visual={visual} lines={lines} />;
    case 'table': return <Table visual={visual} lines={lines} theme={theme} />;
    case 'steps': return <Steps visual={visual} lines={lines} />;
    case 'scorecard': return <Scorecard visual={visual} lines={lines} />;
    case 'cards': return <Cards visual={visual} lines={lines} />;
  }
};
